import { useEffect, useMemo, useState } from "react";
import { Save, RefreshCw, FileJson, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useCMSReload } from "../lib/cms";
import MediaPicker from "./MediaPicker";

const ALL_SECTIONS = [
  { key: "home",       label: "Homepage",   hint: "Hero, featured properties, about, luxury highlights — every block." },
  { key: "brand",      label: "Brand",      hint: "Logo, wordmark, contact details and social links." },
  { key: "navigation", label: "Navigation", hint: "Top-bar items and the Book Direct CTA label." },
  { key: "footer",     label: "Footer",     hint: "Closing letter copy, footer CTAs and signoff." },
  { key: "seo",        label: "SEO",        hint: "<title>, meta description, Open Graph image, keywords." },
];

const isObject = (v) => v && typeof v === "object" && !Array.isArray(v);
const isPrimArray = (v) => Array.isArray(v) && v.every((x) => typeof x === "string" || typeof x === "number");
const IMAGE_FIELD_RE = /(^|_|\.)(image|images|img|photo|photos|logo|logo_url|og_image|og|cover|hero|thumb|thumbnail|banner|picture)$/i;
const isImageField = (name) => typeof name === "string" && IMAGE_FIELD_RE.test(name);

// Resolve /api/uploads/<file> against REACT_APP_BACKEND_URL so thumbnails
// render inside the admin (frontend origin ≠ backend origin in preview).
const resolveImg = (u) => {
  if (!u) return u;
  if (/^https?:\/\//.test(u)) return u;
  const base = (API || "").replace(/\/api$/, "");
  return `${base}${u}`;
};

const inputClass =
  "w-full bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] text-[var(--ed-ink)] px-3 py-2.5 text-sm outline-none rounded-sm transition-colors";
const labelClass = "block text-[0.62rem] uppercase tracking-[0.22em] text-[var(--ed-ink-muted)] mb-1.5";

function FieldEditor({ value, onChange, name, depth = 0 }) {
  if (typeof value === "boolean") return BoolField({ value, onChange, name });
  if (typeof value === "number") return NumberField({ value, onChange, name });
  if (value === null || value === undefined || typeof value === "string") {
    if (isImageField(name)) return ImageField({ value: value || "", onChange, name });
    return StringField({ value: value || "", onChange, name });
  }
  if (isPrimArray(value)) {
    if (isImageField(name)) return ImageArrayField({ value, onChange, name });
    return PrimArrayField({ value, onChange, name });
  }
  if (Array.isArray(value)) return ObjectArrayField({ value, onChange, name, depth });
  if (isObject(value)) return ObjectField({ value, onChange, name, depth });
  return null;
}

function ImageField({ value, onChange, name }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const preview = resolveImg(value);
  return (
    <div>
      <label className={labelClass}>{name}</label>
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 flex-shrink-0 border border-[var(--ed-rule)] bg-[var(--ed-paper)] rounded-sm overflow-hidden flex items-center justify-center">
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImagePlus size={18} className="text-[var(--ed-ink-muted)]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <input
            data-testid={`cms-image-${name}`}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Pick from Media Library or paste an image URL"
            className={inputClass}
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              data-testid={`cms-image-pick-${name}`}
              onClick={() => setPickerOpen(true)}
              className="text-[0.6rem] uppercase tracking-[0.22em] px-3 py-1.5 bg-[var(--ed-emerald)] text-white hover:bg-[var(--ed-emerald-deep)] rounded-sm flex items-center gap-1.5"
            >
              <ImagePlus size={11} /> Pick from Media Library
            </button>
            {value && (
              <button
                type="button"
                data-testid={`cms-image-clear-${name}`}
                onClick={() => onChange("")}
                className="text-[0.6rem] uppercase tracking-[0.22em] px-3 py-1.5 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] rounded-sm"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(url) => onChange(url)}
        currentValue={value}
      />
    </div>
  );
}

function ImageArrayField({ value, onChange, name }) {
  const [pickerOpen, setPickerOpen] = useState(-1);
  const setAt = (i, url) => {
    const next = value.slice();
    next[i] = url;
    onChange(next);
  };
  const removeAt = (i) => onChange(value.filter((_, j) => j !== i));
  const addSlot = () => onChange(value.concat([""]));
  return (
    <div>
      <p className={labelClass}>{name} <span className="normal-case tracking-normal text-[var(--ed-ink-muted)]">({value.length} slot{value.length === 1 ? "" : "s"})</span></p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {value.map((url, i) => (
          <div key={i} className="border border-[var(--ed-rule)] rounded-sm overflow-hidden bg-[var(--ed-paper)]/40" data-testid={`cms-img-slot-${name}-${i}`}>
            <div className="aspect-square bg-[var(--ed-paper)] relative flex items-center justify-center overflow-hidden">
              {url ? (
                <img src={resolveImg(url)} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImagePlus size={18} className="text-[var(--ed-ink-muted)]" />
              )}
              <span className="absolute top-1.5 left-1.5 bg-[var(--ed-ink)]/70 text-white text-[0.55rem] tracking-widest uppercase px-1.5 py-0.5">#{i + 1}</span>
            </div>
            <div className="p-2 flex gap-1.5">
              <button
                type="button"
                onClick={() => setPickerOpen(i)}
                data-testid={`cms-img-pick-${name}-${i}`}
                className="flex-1 text-[0.55rem] uppercase tracking-widest px-2 py-1.5 bg-[var(--ed-emerald)] text-white hover:bg-[var(--ed-emerald-deep)] rounded-sm"
              >
                {url ? "Change" : "Pick"}
              </button>
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="text-[0.55rem] px-2 py-1.5 border border-red-200 hover:border-red-500 text-red-500 hover:bg-red-50 rounded-sm"
                aria-label="Remove slot"
              >
                <ImagePlus size={11} style={{ transform: "rotate(45deg)" }} />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addSlot}
          data-testid={`cms-img-add-${name}`}
          className="aspect-square border-2 border-dashed border-[var(--ed-rule)] hover:border-[var(--ed-emerald)] text-[var(--ed-ink-muted)] hover:text-[var(--ed-emerald)] flex flex-col items-center justify-center gap-1.5 rounded-sm text-[0.6rem] uppercase tracking-widest"
        >
          <ImagePlus size={18} />
          Add slot
        </button>
      </div>
      <MediaPicker
        open={pickerOpen >= 0}
        onClose={() => setPickerOpen(-1)}
        onPick={(url) => { setAt(pickerOpen, url); setPickerOpen(-1); }}
        currentValue={pickerOpen >= 0 ? value[pickerOpen] : ""}
      />
    </div>
  );
}

function BoolField({ value, onChange, name }) {
  return (
    <label className="flex items-center gap-3 text-sm text-[var(--ed-ink-soft)] py-2">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[var(--ed-emerald)]" />
      <span>{name}</span>
    </label>
  );
}
function NumberField({ value, onChange, name }) {
  return (
    <div>
      <label className={labelClass}>{name}</label>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        className={inputClass}
      />
    </div>
  );
}
function StringField({ value, onChange, name }) {
  const multiline = value.includes("\n") || value.length > 80;
  const rows = Math.min(8, Math.max(3, value.split("\n").length + 1));
  if (multiline) {
    return (
      <div>
        <label className={labelClass}>{name}</label>
        <textarea
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputClass} font-mono leading-relaxed resize-y`}
        />
      </div>
    );
  }
  return (
    <div>
      <label className={labelClass}>{name}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} />
    </div>
  );
}
function PrimArrayField({ value, onChange, name }) {
  return (
    <div>
      <label className={labelClass}>
        {name} <span className="normal-case tracking-normal text-[var(--ed-ink-muted)]">(comma separated)</span>
      </label>
      <input
        type="text"
        value={value.join(", ")}
        onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
        className={inputClass}
      />
    </div>
  );
}
function ObjectArrayField({ value, onChange, name, depth }) {
  const replace = (i, nv) => { const next = value.slice(); next[i] = nv; onChange(next); };
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  const add = () => {
    const tmpl = value[0];
    let blank = "";
    if (tmpl != null && typeof tmpl === "object") {
      blank = {};
      Object.keys(tmpl).forEach((k) => { blank[k] = typeof tmpl[k] === "number" ? 0 : ""; });
    }
    onChange(value.concat([blank]));
  };
  const items = value.map((item, i) => (
    <ArrayItemRow key={i} index={i} item={item} depth={depth} onReplace={replace} onRemove={remove} />
  ));
  return (
    <div className={depth > 0 ? "border-l border-[var(--ed-rule)] pl-4" : ""}>
      <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[var(--ed-ink-muted)] mb-3">
        {name} <span className="text-[var(--ed-ink-muted)]">[{value.length}]</span>
      </p>
      <div className="space-y-4">
        {items}
        <button
          type="button"
          onClick={add}
          className="text-[0.6rem] uppercase tracking-[0.22em] border border-[var(--ed-rule)] hover:border-[var(--ed-emerald)] hover:text-[var(--ed-emerald)] text-[var(--ed-ink-soft)] px-4 py-2 rounded-sm"
        >
          + Add item
        </button>
      </div>
    </div>
  );
}

function ArrayItemRow({ index, item, depth, onReplace, onRemove }) {
  return (
    <div className="border border-[var(--ed-rule)] rounded-sm p-4 bg-[var(--ed-paper)]/40">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[var(--ed-emerald)] tracking-widest font-medium">#{index + 1}</p>
        <button type="button" onClick={() => onRemove(index)} className="text-[0.6rem] text-red-500 hover:text-red-700 uppercase tracking-widest">
          Remove
        </button>
      </div>
      <FieldEditor value={item} name="item" depth={depth + 1} onChange={(nv) => onReplace(index, nv)} />
    </div>
  );
}

function ObjectField({ value, onChange, name, depth }) {
  const handleKeyChange = (k, nv) => onChange({ ...value, [k]: nv });
  const entries = Object.entries(value);
  return (
    <div className={(depth > 0 ? "border-l border-[var(--ed-rule)] pl-4 " : "") + "space-y-4"}>
      {depth > 0 && <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[var(--ed-emerald)]">{name}</p>}
      {entries.map(([k, v]) => (
        <ObjectKeyRow key={k} keyName={k} kvalue={v} depth={depth} onUpdate={handleKeyChange} />
      ))}
    </div>
  );
}

function ObjectKeyRow({ keyName, kvalue, depth, onUpdate }) {
  return <FieldEditor value={kvalue} name={keyName} depth={depth + 1} onChange={(nv) => onUpdate(keyName, nv)} />;
}

export default function CMSEditor({ sections, defaultActive }) {
  const SECTIONS = useMemo(
    () => (sections && sections.length ? ALL_SECTIONS.filter((s) => sections.includes(s.key)) : ALL_SECTIONS),
    [sections]
  );
  const [active, setActive] = useState(defaultActive || SECTIONS[0]?.key || "home");
  const [content, setContent] = useState({});
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [showJSON, setShowJSON] = useState(false);
  const reloadCMS = useCMSReload();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(SECTIONS.map((s) => api.get(`/site_content/${s.key}`)));
      const next = {};
      results.forEach((r, i) => { next[SECTIONS[i].key] = r.data; });
      setContent(next);
      setDirty({});
    } catch {
      toast.error("Could not load site content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [SECTIONS]);

  const onFieldChange = (key, newData) => {
    setContent((prev) => ({ ...prev, [key]: { ...prev[key], data: newData } }));
    setDirty((prev) => ({ ...prev, [key]: true }));
  };

  const save = async (key) => {
    try {
      await api.put(`/site_content/${key}`, { data: content[key]?.data || {} });
      toast.success(`Saved ${key}`);
      setDirty((prev) => { const n = { ...prev }; delete n[key]; return n; });
      reloadCMS();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const activeSection = SECTIONS.find((s) => s.key === active);
  const activeData = content[active]?.data || {};
  const isEmpty = Object.keys(activeData).length === 0;

  return (
    <div data-testid="cms-editor" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {SECTIONS.length > 1 && (
        <aside className="lg:col-span-3">
          <div className="border border-[var(--ed-rule)] rounded-sm p-1.5 lg:sticky lg:top-6 bg-[var(--ed-bg)]">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                data-testid={`cms-tab-${s.key}`}
                onClick={() => setActive(s.key)}
                className={`w-full text-left px-4 py-3 rounded-sm transition-colors flex items-center justify-between ${
                  active === s.key
                    ? "bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)]"
                    : "text-[var(--ed-ink-soft)] hover:bg-[var(--ed-paper)] hover:text-[var(--ed-ink)]"
                }`}
              >
                <div>
                  <p className="text-sm font-medium">{s.label}</p>
                  <p className="text-[0.65rem] text-[var(--ed-ink-muted)] mt-0.5">{s.key}</p>
                </div>
                {dirty[s.key] ? <span className="w-2 h-2 rounded-full bg-amber-500" /> : null}
              </button>
            ))}
          </div>
        </aside>
      )}

      <div className={SECTIONS.length > 1 ? "lg:col-span-9" : "lg:col-span-12"}>
        {loading ? (
          <p className="text-[var(--ed-ink-muted)] py-16 text-center">Loading…</p>
        ) : (
          <div className="border border-[var(--ed-rule)] rounded-sm overflow-hidden bg-[var(--ed-bg)]">
            <div className="flex items-center justify-between px-6 py-4 bg-[var(--ed-paper)]/50 border-b border-[var(--ed-rule)] flex-wrap gap-3">
              <div>
                <h2 className="font-serif text-xl text-[var(--ed-ink)]">{activeSection?.label}</h2>
                <p className="text-xs text-[var(--ed-ink-muted)] mt-0.5">{activeSection?.hint}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  data-testid="cms-toggle-json"
                  onClick={() => setShowJSON((v) => !v)}
                  className="text-[0.6rem] uppercase tracking-[0.22em] border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] px-3 py-2 rounded-sm flex items-center gap-1.5"
                >
                  <FileJson size={11} /> {showJSON ? "Form view" : "JSON view"}
                </button>
                <button
                  onClick={fetchAll}
                  className="text-[0.6rem] uppercase tracking-[0.22em] border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] px-3 py-2 rounded-sm flex items-center gap-1.5"
                >
                  <RefreshCw size={11} /> Refresh
                </button>
                <button
                  data-testid={`cms-save-${active}`}
                  onClick={() => save(active)}
                  disabled={!dirty[active]}
                  className={
                    "text-[0.6rem] uppercase tracking-[0.22em] px-4 py-2 rounded-sm flex items-center gap-1.5 " +
                    (dirty[active]
                      ? "bg-[var(--ed-emerald)] text-white hover:bg-[var(--ed-emerald-deep)]"
                      : "bg-[var(--ed-paper)] text-[var(--ed-ink-muted)] cursor-not-allowed")
                  }
                >
                  <Save size={11} /> Save changes
                </button>
              </div>
            </div>

            <div className="p-6">
              {showJSON ? (
                <textarea
                  data-testid="cms-json-editor"
                  rows={28}
                  value={JSON.stringify(activeData, null, 2)}
                  onChange={(e) => { try { onFieldChange(active, JSON.parse(e.target.value)); } catch { /* ignore */ } }}
                  className={`${inputClass} font-mono leading-relaxed resize-y`}
                />
              ) : isEmpty ? (
                <p className="text-[var(--ed-ink-muted)] text-sm">
                  This section is empty. Switch to <strong>JSON view</strong> to paste a structure, or save once to seed defaults.
                </p>
              ) : (
                <FieldEditor value={activeData} name={active} depth={0} onChange={(nv) => onFieldChange(active, nv)} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
